import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useCustomers } from "@/hooks/use-customers";
import {
  useAddOfferTemplateLines,
  useOfferLines,
  useOfferTemplates,
  useUpdateOffer,
  type Offer,
} from "@/hooks/use-offers";
import { NewCustomerDialog } from "@/components/new-project/NewCustomerDialog";
import { SrIcon } from "@/components/ui/SrIcon";
import {
  InvoiceTermsEditor,
  DEFAULT_INVOICE_TERMS,
  type InvoiceTerm,
} from "@/components/offer/InvoiceTermsEditor";
import {
  OfferWritersEditor,
  type Writer,
} from "@/components/offer/OfferWritersEditor";

interface Props {
  offer: Offer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SizeCode = "none" | "xs" | "s" | "m" | "l";

export function OfferSettingsDialog({ offer, open, onOpenChange }: Props) {
  const { data: customers = [] } = useCustomers();
  const { data: templates = [] } = useOfferTemplates();
  const { data: lines = [] } = useOfferLines(offer.id);
  const updateOffer = useUpdateOffer();
  const addTemplateLines = useAddOfferTemplateLines();

  const writingLine = lines.find(
    (l) => l.is_auto_generated && l.auto_kind === "offer_writing",
  );

  const initialWriters: Writer[] =
    Array.isArray(offer.writers) && offer.writers.length > 0
      ? offer.writers
      : [];
  const [writers, setWriters] = useState<Writer[]>(initialWriters);

  const [customerId, setCustomerId] = useState<string | null>(offer.customer_id);
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [size, setSize] = useState<SizeCode>("none");
  const [includeStills, setIncludeStills] = useState(false);
  const [includeReise, setIncludeReise] = useState(false);
  const [showInvoiceTerms, setShowInvoiceTerms] = useState(
    offer.show_invoice_terms !== false,
  );
  const initialTerms =
    Array.isArray(offer.invoice_terms) && offer.invoice_terms.length > 0
      ? offer.invoice_terms
      : DEFAULT_INVOICE_TERMS;
  const [invoiceTerms, setInvoiceTerms] = useState<InvoiceTerm[]>(initialTerms);
  // VIB-993: reise faktureres separat (medgått) — settes på tilbudet.
  const [billsTravelPerMedgatt, setBillsTravelPerMedgatt] = useState(
    offer.bills_travel_per_medgatt ?? false,
  );

  useEffect(() => {
    if (open) {
      setCustomerId(offer.customer_id);
      setSize("none");
      setIncludeStills(false);
      setIncludeReise(false);
      setShowInvoiceTerms(offer.show_invoice_terms !== false);
      setBillsTravelPerMedgatt(offer.bills_travel_per_medgatt ?? false);
      setInvoiceTerms(
        Array.isArray(offer.invoice_terms) && offer.invoice_terms.length > 0
          ? offer.invoice_terms
          : DEFAULT_INVOICE_TERMS,
      );
      setWriters(
        Array.isArray(offer.writers) && offer.writers.length > 0
          ? offer.writers
          : [],
      );
    }
  }, [
    open,
    offer.customer_id,
    offer.show_invoice_terms,
    offer.invoice_terms,
    offer.writers,
    offer.bills_travel_per_medgatt,
  ]);

  const sizeTemplates = templates.filter((t) => t.kind === "size");
  const hasStills = templates.some((t) => t.code === "stills");
  const hasReise = templates.some((t) => t.code === "reise");

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const customerChanged = customerId !== offer.customer_id;
  const showInvoiceTermsChanged =
    showInvoiceTerms !== (offer.show_invoice_terms !== false);
  const termsListChanged =
    JSON.stringify(invoiceTerms) !== JSON.stringify(initialTerms);
  const writersChanged =
    JSON.stringify(writers) !== JSON.stringify(initialWriters);
  const billsTravelChanged =
    billsTravelPerMedgatt !== (offer.bills_travel_per_medgatt ?? false);
  const invoiceTermsSum = invoiceTerms.reduce(
    (acc, t) => acc + (Number(t.pct) || 0),
    0,
  );
  const invoiceTermsValid =
    !showInvoiceTerms || Math.abs(invoiceTermsSum - 100) < 0.01;
  const writersSumPct = writers.reduce((s, w) => s + (w.share_pct ?? 0), 0);
  const writersValid =
    writers.length === 0 || Math.abs(writersSumPct - 100) < 0.01;
  const templateCodes: string[] = [
    ...(size !== "none" ? [size] : []),
    ...(includeStills && hasStills ? ["stills"] : []),
    ...(includeReise && hasReise ? ["reise"] : []),
  ];
  const hasTemplateChange = templateCodes.length > 0;
  const canSave =
    invoiceTermsValid &&
    writersValid &&
    (customerChanged ||
      showInvoiceTermsChanged ||
      termsListChanged ||
      writersChanged ||
      billsTravelChanged ||
      hasTemplateChange);
  const isSaving = updateOffer.isPending || addTemplateLines.isPending;

  async function handleSave() {
    if (!canSave) return;
    try {
      const hasOfferPatch =
        customerChanged ||
        showInvoiceTermsChanged ||
        termsListChanged ||
        writersChanged ||
        billsTravelChanged;
      if (hasOfferPatch) {
        await updateOffer.mutateAsync({
          id: offer.id,
          patch: {
            ...(customerChanged
              ? {
                  customer_id: customerId,
                  customer_name: selectedCustomer?.name ?? null,
                }
              : {}),
            ...(showInvoiceTermsChanged
              ? { show_invoice_terms: showInvoiceTerms }
              : {}),
            ...(termsListChanged ? { invoice_terms: invoiceTerms } : {}),
            ...(writersChanged ? { writers } : {}),
            ...(billsTravelChanged
              ? { bills_travel_per_medgatt: billsTravelPerMedgatt }
              : {}),
          },
        });
      }
      if (hasTemplateChange) {
        const inserted = await addTemplateLines.mutateAsync({
          offerId: offer.id,
          templateCodes,
        });
        toast.success(
          inserted > 0
            ? `${inserted} mal-linjer lagt til`
            : "Ingen linjer i valgt mal",
        );
      } else if (hasOfferPatch) {
        toast.success("Tilbud oppdatert");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Tilbudsinnstillinger</DialogTitle>
          <DialogDescription>
            Bytt kunde eller legg til linjer fra en tilbudsmal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Kunde</Label>
            <div className="flex gap-2">
              <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="flex-1 justify-between font-normal"
                  >
                    {selectedCustomer?.name ?? offer.customer_name ?? "Velg kunde…"}
                    <SrIcon
                      name="chevrons-up-down"
                      className="ml-2 h-4 w-4 shrink-0 opacity-50"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Søk kunder…" />
                    <CommandList>
                      <CommandEmpty>Ingen treff</CommandEmpty>
                      <CommandGroup>
                        {customers.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setCustomerId(c.id);
                              setCustomerPopoverOpen(false);
                            }}
                          >
                            <SrIcon
                              name="check"
                              className={cn(
                                "mr-2 h-4 w-4",
                                customerId === c.id ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {c.name}
                            {c.country_code && c.country_code !== "NO" && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {c.country_code}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <NewCustomerDialog onCustomerCreated={(id) => setCustomerId(id)} />
            </div>
          </div>

          {sizeTemplates.length > 0 && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div>
                <Label className="text-sm font-medium">Legg til fra mal</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Mal-linjer legges til etter eksisterende linjer. Eksisterende
                  linjer påvirkes ikke.
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { value: "none" as SizeCode, label: "Ingen" },
                    ...sizeTemplates.map((t) => ({
                      value: t.code as SizeCode,
                      label: t.name,
                    })),
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setSize(opt.value)}
                      className={cn(
                        "rounded-md border bg-background px-2 py-1.5 text-sm transition-colors hover:bg-muted/50",
                        size === opt.value &&
                          "border-primary ring-1 ring-primary bg-primary/5 font-medium",
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {(hasStills || hasReise) && (
                <div>
                  <Label className="text-sm font-medium">Tillegg</Label>
                  <div className="mt-2 flex gap-4">
                    {hasStills && (
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={includeStills}
                          onChange={(e) => setIncludeStills(e.target.checked)}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        Stills
                      </label>
                    )}
                    {hasReise && (
                      <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={includeReise}
                          onChange={(e) => setIncludeReise(e.target.checked)}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        Reise
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={showInvoiceTerms}
                onChange={(e) => setShowInvoiceTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
              />
              <span>
                <span className="font-medium">Inkluder faktureringsvilkår</span>
                <span className="block text-xs text-muted-foreground">
                  Vises som fotnote i kopier-bilde og Keynote.
                </span>
              </span>
            </label>

            {showInvoiceTerms && (
              <InvoiceTermsEditor terms={invoiceTerms} onChange={setInvoiceTerms} />
            )}

            {/* VIB-993: reise faktureres separat (medgått). */}
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={billsTravelPerMedgatt}
                onChange={(e) => setBillsTravelPerMedgatt(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
              />
              <span>
                <span className="font-medium">Reise faktureres separat (medgått)</span>
                <span className="block text-xs text-muted-foreground">
                  Vises som faktureringsvilkår i tilbudet og overføres til prosjektet ved konvertering.
                </span>
              </span>
            </label>
          </div>

          <OfferWritersEditor
            writers={writers}
            onChange={setWriters}
            totalHours={writingLine?.quantity}
            disabled={offer.status === "accepted"}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving && (
              <SrIcon name="loader2" className="mr-2 h-4 w-4 animate-spin" />
            )}
            Lagre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
